import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import type { DossierNote, DossierNoteRow, User } from "../types";
import { dossierNoteRowToDossierNote } from "../types";

type UseDossierNotesOptions = {
  dossierId?: string;
  currentUser?: User | null;
};

export function useDossierNotes({ dossierId, currentUser }: UseDossierNotesOptions) {
  const [notes, setNotes] = useState<DossierNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isExternalActor =
    currentUser?.role === "partner" ||
    currentUser?.role === "installer" ||
    currentUser?.role === "commercial";
  /** Partenaire / poseur / commercial : toujours passer par l'Edge Function (code) pour lire/écrire les notes.
   *  La RLS directe échoue souvent quand auth.uid() ≠ profiles.id (magic link). */
  const useCodeFallback = isExternalActor && Boolean(currentUser?.code?.trim());

  const fetchNotesByCode = useCallback(async () => {
    const normalizedDossierId = dossierId?.trim();
    if (!normalizedDossierId || !currentUser?.code || !isExternalActor) {
      setNotes([]);
      return;
    }
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "get-dossier-notes-by-code",
        {
          body: {
            code: currentUser.code,
            role: currentUser.role,
            dossierId: normalizedDossierId,
          },
        }
      );
      if (fnError) throw fnError;
      const raw = (data as { notes?: DossierNoteRow[]; error?: string } | null) ?? {};
      if (raw.error) throw new Error(raw.error);
      setNotes(((raw.notes ?? []) as DossierNoteRow[]).map(dossierNoteRowToDossierNote));
    } catch (err) {
      throw err;
    }
  }, [currentUser?.code, currentUser?.role, dossierId, isExternalActor]);

  const fetchNotes = useCallback(async () => {
    const normalizedDossierId = dossierId?.trim();
    if (!normalizedDossierId) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (useCodeFallback) {
        await fetchNotesByCode();
      } else {
        const { data, error: fetchError } = await supabase
          .from("dossier_notes")
          .select("*")
          .eq("dossier_id", normalizedDossierId)
          .order("created_at", { ascending: false });
        if (fetchError) throw fetchError;
        setNotes(((data ?? []) as DossierNoteRow[]).map(dossierNoteRowToDossierNote));
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : (err && typeof err === "object" && "message" in err)
            ? String((err as { message: unknown }).message)
            : "Erreur chargement notes";
      setError(message);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [dossierId, fetchNotesByCode, useCodeFallback]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    const normalizedDossierId = dossierId?.trim();
    if (!normalizedDossierId || useCodeFallback) return;
    const channel = supabase
      .channel(`dossier-notes-${normalizedDossierId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "dossier_notes",
          filter: `dossier_id=eq.${normalizedDossierId}`,
        },
        () => {
          fetchNotes();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [dossierId, fetchNotes, useCodeFallback]);

  const addNote = useCallback(
    async (content: string) => {
      const normalizedDossierId = dossierId?.trim();
      const trimmedContent = content.trim();
      if (!normalizedDossierId || !currentUser || !trimmedContent) {
        return { data: null, error: "Impossible d'ajouter la note." };
      }

      setSubmitting(true);
      setError(null);
      try {
        let inserted: DossierNote;
        if (useCodeFallback) {
          const { data, error: fnError } = await supabase.functions.invoke(
            "create-dossier-note-by-code",
            {
              body: {
                code: currentUser.code,
                role: currentUser.role,
                dossierId: normalizedDossierId,
                content: trimmedContent,
              },
            }
          );
          if (fnError) throw fnError;
          const raw = (data as { data?: DossierNoteRow | null; error?: string } | null) ?? {};
          if (raw.error) throw new Error(raw.error);
          if (!raw.data) throw new Error("Aucune note retournee par le serveur.");
          inserted = dossierNoteRowToDossierNote(raw.data);
        } else {
          const row: Record<string, string> = {
            dossier_id: normalizedDossierId,
            user_id: currentUser.id,
            user_role: currentUser.role,
            user_name: currentUser.name,
            content: trimmedContent,
          };

          const { data, error: insertError } = await supabase
            .from("dossier_notes")
            .insert(row)
            .select("*")
            .single();
          if (insertError) throw insertError;
          inserted = dossierNoteRowToDossierNote(data as DossierNoteRow);
        }

        setNotes((prev) => [inserted, ...prev]);
        return { data: inserted, error: null };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : (err && typeof err === "object" && "message" in err)
              ? String((err as { message: unknown }).message)
              : "Erreur ajout note";
        setError(message);
        return { data: null, error: message };
      } finally {
        setSubmitting(false);
      }
    },
    [currentUser, dossierId, useCodeFallback]
  );

  const updateNote = useCallback(
    async (noteId: string, newContent: string) => {
      const trimmedNoteId = noteId.trim();
      const trimmedContent = newContent.trim();
      if (!trimmedNoteId || !trimmedContent || !currentUser) {
        return { data: null, error: "Impossible de modifier la note." };
      }

      setError(null);
      try {
        let updated: DossierNote;
        if (useCodeFallback) {
          const { data, error: fnError } = await supabase.functions.invoke(
            "update-dossier-note-by-code",
            {
              body: {
                code: currentUser.code,
                role: currentUser.role,
                noteId: trimmedNoteId,
                content: trimmedContent,
              },
            }
          );
          if (fnError) throw fnError;
          const raw = (data as { data?: DossierNoteRow | null; error?: string } | null) ?? {};
          if (raw.error) throw new Error(raw.error);
          if (!raw.data) throw new Error("Aucune note retournee par le serveur.");
          updated = dossierNoteRowToDossierNote(raw.data);
        } else {
          const { data, error: updateError } = await supabase
            .from("dossier_notes")
            .update({ content: trimmedContent })
            .eq("id", trimmedNoteId)
            .select("*")
            .single();
          if (updateError) throw updateError;
          updated = dossierNoteRowToDossierNote(data as DossierNoteRow);
        }

        setNotes((prev) => prev.map((note) => (note.id === updated.id ? updated : note)));
        return { data: updated, error: null };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : (err && typeof err === "object" && "message" in err)
              ? String((err as { message: unknown }).message)
              : "Erreur modification note";
        setError(message);
        return { data: null, error: message };
      }
    },
    [currentUser, useCodeFallback]
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const trimmedNoteId = noteId.trim();
      if (!trimmedNoteId || !currentUser) {
        return { error: "Impossible de supprimer la note." };
      }

      setError(null);
      try {
        if (useCodeFallback) {
          const { data, error: fnError } = await supabase.functions.invoke(
            "delete-dossier-note-by-code",
            {
              body: {
                code: currentUser.code,
                role: currentUser.role,
                noteId: trimmedNoteId,
              },
            }
          );
          if (fnError) throw fnError;
          const raw = (data as { error?: string } | null) ?? {};
          if (raw.error) throw new Error(raw.error);
        } else {
          const { error: deleteError } = await supabase
            .from("dossier_notes")
            .delete()
            .eq("id", trimmedNoteId);
          if (deleteError) throw deleteError;
        }

        setNotes((prev) => prev.filter((note) => note.id !== trimmedNoteId));
        return { error: null };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : (err && typeof err === "object" && "message" in err)
              ? String((err as { message: unknown }).message)
              : "Erreur suppression note";
        setError(message);
        return { error: message };
      }
    },
    [currentUser, useCodeFallback]
  );

  return {
    notes,
    loading,
    submitting,
    error,
    refetch: fetchNotes,
    addNote,
    updateNote,
    deleteNote,
  };
}
